import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Typography } from "@mui/material";

// custom components
import CustomLayout from "../../../components/layout";
import SupportingDocs from "../../../components/supporting-docs";
import CustomDivider from "../../../components/custom-divider";
import PrimaryButton from "../../../components/buttons/primary-button";
import SecondaryButton from "../../../components/buttons/secondary-button";
import OrganizationDropdown from "../../../components/organization-dropdown";
import Body3 from "../../../components/typography/body-03";
import Body2 from "../../../components/typography/body-02";
import UploadHistoryTable from "../../../components/custom-table/upload-history-table";

// helper utils
import { formatBytes } from "../../../utils/format-bytes";
import { numericKeys } from "../../../utils/common-helpers";
import { VERBIAGE_CONSTANTS } from "../../../utils/verbiage.constants";

// api slices
import { useConvertMismoMutation } from "../../../features/convert-mismo/convertMismoApiSlice";
import { useUploadV3XmlMutation } from "../../../features/upload-xml/uploadV3XmlApiSlice";
import { useDeletePendingApplicationMutation } from "../../../features/delete-pending-application/deletePendingApplicationApiSlice";
import { useUploadXmlHistoryMutation } from "../../../features/upload-xml/uploadXmlHistoryApiSlice";
import { useGetMaxLoanMutation } from "../../../features/upload-xml/getMaxLoanAmountApiSlice";

// reducer slices
import {
	selectOrganizationName,
	selectUserDetails,
	selectSelectedOrganization,
} from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";
import { initLoan, resumeLoan } from "../../../features/current-loan/currentLoanSlice";
import { getUploadHistoryItemGuid } from "../../../utils/upload-history-helpers";

// custom modal
import ProcessingModal from "../../../components/modal/processing-modal";
import ConfirmationModal from "../../../components/modal/confirmation-modal";
import SuccessModal from "../../../components/modal/success-modal";
import MaxLoanModal from "../../../components/modal/max-loan-amount-check-modal";

// mui icons
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadOutlined from "@mui/icons-material/UploadOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import JSZip from "jszip";
import styles from "./index.module.scss";

const fileTypes = ["XML"];

export default function CurrentLoanUpload() {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);

	const [orgSelected, setOrgSelected] = useState(false);

	useEffect(() => {
		const isMultiOrg = userDetails?.organizations?.length > 1;
		if (!isMultiOrg) {
			setOrgSelected(true);
		} else {
			setOrgSelected(!!selectedOrganization && selectedOrganization !== "");
		}
	}, [selectedOrganization, userDetails]);

	const shouldDisableButtons = !orgSelected;

	const [uploadV3Xml] = useUploadV3XmlMutation();
	const [convertMismo] = useConvertMismoMutation();
	const [uploadHistoryDataCall] = useUploadXmlHistoryMutation();
	const [deletePendingApplication] = useDeletePendingApplicationMutation();
	const [getMaxLoanMutationCall] = useGetMaxLoanMutation();

	const [addCounter, setAddCounter] = useState([1, 2]);
	const [confirmModalText, setConfirmModalText] = useState("");
	const [showUploadHistory, setShowUploadHistory] = useState(true);
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);
	const [uploadHistoryCount, setUploadHistoryCount] = useState(0);
	const [uploadHistoryLoading, setUploadHistoryLoading] = useState(true);
	const [uploadRefreshTrigger, setUploadRefreshTrigger] = useState(0);
	const [newLoanAmount, setNewLoanAmount] = useState(0);
	const [maxLoanAmountConstant, setMaxLoanAmountConstant] = useState(0);
	const [maxLoanCheckModal, setMaxLoanCheckModal] = useState(false);

	const [file, setFile] = useState("");
	const [fileUploadModal, setFileUploadModal] = useState(false);
	const [xmlMultiUploaded, setMultiXmlUploaded] = useState(false);
	const [activeUploadView, setActiveUploadView] = useState("history");
	const [fileUploadedModal, setFileUploadedModal] = useState(false);
	const [loanData, setLoanData] = useState({});
	const [multiLoanData, setMultiLoanData] = useState({});
	const [currentItemSet, setCurrentItemSet] = useState(0);
	const [showLoanHistoryNumber, setShowLoanHistoryNumber] = useState({});
	const [handleDeleteRowModel, setHandleDeleteRowModel] = useState(false);
	const [addItemToDelete, setAddItemToDelete] = useState(0);

	const handleGetMaxLoanAmount = async () => {
		try {
			const loanAmountData = await getMaxLoanMutationCall().unwrap();
			setMaxLoanAmountConstant(loanAmountData?.response?.data?.max_loan_amount);
		} catch (err) {
			console.log("getMaxLoanMutationCall:", err);
		}
	};

	useEffect(() => {
		if (
			userDetails?.user_roles?.includes("admin") &&
			(organizationName === undefined || organizationName === null || organizationName === "")
		) {
			setConfirmModalText(VERBIAGE_CONSTANTS.SIGN_IN_BROKER);
			setConfirmModalVisible(true);
		}
		handleGetMaxLoanAmount();
	}, [organizationName, userDetails]);

	const lastFetchKeyRef = useRef({ userId: null, refresh: null });
	useEffect(() => {
		const tpoUserId = userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id;
		const refreshKey = uploadRefreshTrigger;
		if (!tpoUserId) return;
		const isSame =
			lastFetchKeyRef.current.userId === tpoUserId &&
			lastFetchKeyRef.current.refresh === refreshKey;
		if (isSame) return;
		lastFetchKeyRef.current = { userId: tpoUserId, refresh: refreshKey };
		const fetchUploadCount = async () => {
			try {
				setUploadHistoryLoading(true);
				const response = await uploadHistoryDataCall({
					tpo_user_id: tpoUserId,
					page: 1,
					pageSize: 25,
					filters: {},
					userDetails,
					selectedOrganization,
				}).unwrap();
				let count = 0;
				if (Array.isArray(response?.response)) count = response.response.length;
				else if (Array.isArray(response?.results)) count = response?.count ?? 0;
				else if (Array.isArray(response)) count = response.length;
				setUploadHistoryCount(count);
			} catch (err) {
				console.error("Error fetching upload count:", err);
			} finally {
				setUploadHistoryLoading(false);
			}
		};
		fetchUploadCount();
	}, [
		userDetails?.user_tpo_id,
		impersonatedUser?.user_tpo_id,
		uploadRefreshTrigger,
		uploadHistoryDataCall,
		selectedOrganization,
	]);

	const organizationId = useSelector((state) => state.auth.organizationId);
	const lastOrganizationIdRef = useRef(null);
	useEffect(() => {
		if (organizationId && organizationId !== lastOrganizationIdRef.current) {
			const previousOrgId = lastOrganizationIdRef.current;
			lastOrganizationIdRef.current = organizationId;
			setUploadRefreshTrigger((prev) => prev + 1);
			// Clear in-progress XML uploads when switching org; files stay on the previous org.
			if (previousOrgId) {
				setFile("");
				setMultiLoanData({});
				setShowLoanHistoryNumber({});
				setAddCounter([1, 2]);
			}
		}
	}, [organizationId]);

	// Navigate to location.state openHistory if set (e.g. from upload history page link)
	useEffect(() => {
		if (location?.state?.openHistory === true) {
			setShowUploadHistory(true);
			setMultiXmlUploaded(false);
			setActiveUploadView("history");
		}
	}, [location]);

	const extractLoanDetails = (data) => {
		const guid = data?.loan_guid || data?.loanGuid || data?.id || "";
		const firstName = data?.applications?.[0]?.borrower?.firstName || "";
		const lastName = data?.applications?.[0]?.borrower?.lastName || "";
		const borrowerName = `${firstName} ${lastName}`.trim() || "Unknown Borrower";
		return { guid, borrowerName };
	};

	const dispatchAndNavigate = (data) => {
		const { guid, borrowerName } = extractLoanDetails(data);
		dispatch(
			initLoan({
				loanGuid: guid || "LN-2024-00847",
				borrowerName: borrowerName || "Unknown Borrower",
				xmlApplicationData: data || null,
			})
		);
		navigate("/current-loan/dashboard");
	};

	const handleOpenFileUploadModal = () => setFileUploadedModal(true);

	const handleCloseFileUploadModal = () => {
		setFileUploadedModal(false);
		const activeData = currentItemSet !== 0 ? multiLoanData[currentItemSet] : loanData;
		if (activeData?.borrowerRequestedLoanAmount > maxLoanAmountConstant) {
			setMaxLoanCheckModal(true);
			return;
		}
		dispatchAndNavigate(activeData);
	};

	const handleCloseMaxLoanCheckModal = () => {
		setMaxLoanCheckModal(false);
		const activeData = currentItemSet !== 0 ? multiLoanData[currentItemSet] : loanData;
		dispatchAndNavigate(activeData);
	};

	const handleNewLoanAmountChange = () => {
		if (newLoanAmount > 0) {
			setLoanData({ ...multiLoanData[currentItemSet], borrowerRequestedLoanAmount: newLoanAmount });
			setMultiLoanData({ ...multiLoanData[currentItemSet], borrowerRequestedLoanAmount: newLoanAmount });
		}
	};

	const showMultiUploadFile = () => {
		setMultiXmlUploaded(true);
		setActiveUploadView("multi");
		setShowUploadHistory(false);
		if (activeUploadView !== "multi") {
			setMultiLoanData({});
			setShowLoanHistoryNumber({});
			setAddCounter([1, 2]);
		}
	};

	const handleUploadHistory = () => {
		setShowUploadHistory(true);
		setMultiXmlUploaded(false);
		setActiveUploadView("history");
		setAddCounter([1, 2]);
		setMultiLoanData({});
		setShowLoanHistoryNumber({});
	};

	const handleManualLoan = () => {
		navigate("/create-loan");
	};

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	const xmlToBinary = (xmlString, filename) =>
		new File([xmlString], filename, { type: "text/xml" });

	const handleMultiXml = async (file, item, suppressModal = false) => {
		if (!suppressModal) setFileUploadModal(true);
		setFile(file);
		if (file.type === "application/x-zip-compressed") {
			await handleZipFileUploadChange(file, suppressModal, item);
			if (!suppressModal) setFileUploadModal(false);
			return;
		}
		var formData = new FormData();
		formData.append("file", file);
		formData.append("tpo_user_id", userDetails?.user_tpo_id);
		// Tags the created LoanApplicationPendingHistoryData row so Upload History
		// can route back into this AURA flow (vs. the legacy edit-loan flow) later.
		formData.append("flow_type", "aura");
		try {
			const fileUpload = await uploadV3Xml({
				formData,
				userDetails,
				selectedOrganization,
			}).unwrap();
			const respData = fileUpload?.data || fileUpload;
			const historyId =
				respData?.loan_history_id ||
				respData?.loanHistoryId ||
				respData?.loan_history?.id ||
				fileUpload?.loan_history_id ||
				fileUpload?.history_id ||
				respData?.id;
			setShowLoanHistoryNumber((prev) => ({ ...prev, [item]: true }));
			setMultiLoanData((prev) => ({ ...prev, [item]: { ...respData, __historyId: historyId } }));
			setUploadRefreshTrigger((prev) => prev + 1);
			await delay(1000);
		} catch (err) {
			console.log("handleMultiXml err:", err);
		} finally {
			if (!suppressModal) setFileUploadModal(false);
		}
	};

	const handleMultipleupload = async (filelist, item) => {
		if (!filelist || filelist.length === 0) return;
		const isBatch = filelist.length > 1;
		if (isBatch) setFileUploadModal(true);
		try {
			if (isBatch) {
				const emptySlots = addCounter.filter((id) => !showLoanHistoryNumber[id]);
				const filesToUse = [];
				let nextNewId = addCounter.length ? Math.max(...addCounter) + 1 : 3;
				for (let i = 0; i < filelist.length; i++) {
					let assignedId;
					if (i < emptySlots.length) {
						assignedId = emptySlots[i];
					} else {
						assignedId = nextNewId;
						nextNewId++;
					}
					filesToUse.push({ file: filelist[i], id: assignedId });
				}
				setAddCounter((prev) => {
					const updated = new Set(prev);
					filesToUse.forEach((f) => updated.add(f.id));
					return Array.from(updated).sort((a, b) => a - b);
				});
				for (let i = 0; i < filesToUse.length; i++) {
					await handleMultiXml(filesToUse[i].file, filesToUse[i].id, true);
					await delay(500);
				}
			} else {
				await handleMultiXml(filelist[0], item);
			}
		} finally {
			if (isBatch) setFileUploadModal(false);
		}
		await delay(500);
	};

	const handleZipFileUploadChange = async (file, suppressModal = false, targetId) => {
		if (!file) return;
		try {
			if (!suppressModal) setFileUploadModal(true);
			const zip = new JSZip();
			const loadedZip = await zip.loadAsync(file);
			const files = [];
			for (const filename in loadedZip.files) {
				if (!loadedZip.files[filename].dir) {
					const content = await loadedZip.files[filename].async("string");
					files.push({ name: filename, content });
				}
			}
			let assignedIds = [];
			let nextIdSeed = addCounter.length ? Math.max(...addCounter) + 1 : 1;
			const targetAvailable = targetId && !showLoanHistoryNumber[targetId];
			for (let i = 0; i < files.length; i++) {
				let id;
				if (i === 0 && targetAvailable) {
					id = targetId;
				} else {
					while (addCounter.includes(nextIdSeed) || showLoanHistoryNumber[nextIdSeed]) nextIdSeed++;
					id = nextIdSeed;
					nextIdSeed++;
				}
				assignedIds.push(id);
			}
			setAddCounter((prev) => {
				const merged = new Set(prev);
				assignedIds.forEach((id) => merged.add(id));
				[1, 2].forEach((ph) => {
					if (!showLoanHistoryNumber[ph] && !assignedIds.includes(ph)) merged.delete(ph);
				});
				return Array.from(merged).sort((a, b) => a - b);
			});
			for (let i = 0; i < files.length; i++) {
				await handleMultiXml(xmlToBinary(files[i].content, files[i].name), assignedIds[i], true);
				await delay(500);
			}
			await delay(500);
		} catch (error) {
			console.error("Error reading zip file:", error);
			alert("Error reading zip file. Please try again.");
		} finally {
			if (!suppressModal) setFileUploadModal(false);
		}
	};

	// A recycled row id would inherit the deleted row's parsed loan data, so the
	// next line always continues past every id the page has already used.
	const getNextRowId = () => {
		const usedIds = [
			...addCounter,
			...numericKeys(multiLoanData),
			...numericKeys(showLoanHistoryNumber),
		];
		return (usedIds.length ? Math.max(...usedIds) : 0) + 1;
	};

	const handleAddRowbutton = () => {
		const nextRowId = getNextRowId();
		setAddCounter((prevRows) => [...prevRows, nextRowId]);
	};

	const handleRemoveRowbutton = (inputitem) => {
		setMultiLoanData(({ [inputitem]: removedLoan, ...restLoans }) => restLoans);
		setShowLoanHistoryNumber(
			({ [inputitem]: removedRow, ...restRows }) => restRows
		);
		setAddCounter((prevRows) => prevRows.filter((item) => item !== inputitem));
		setAddItemToDelete(0);
		setHandleDeleteRowModel(false);
		if (addCounter.length === 1) {
			setShowLoanHistoryNumber({});
			setMultiLoanData({});
			setAddCounter([1, 2]);
			setMultiXmlUploaded(false);
			setShowUploadHistory(false);
		}
	};

	const handleConfirmDeleteRow = async () => {
		const item = addItemToDelete;
		const historyId =
			multiLoanData?.[item]?.__historyId ||
			multiLoanData?.[item]?.loan_history_id ||
			multiLoanData?.[item]?.loanHistoryId ||
			multiLoanData?.[item]?.loan_history?.id ||
			multiLoanData?.[item]?.id;
		setHandleDeleteRowModel(false);
		if (historyId) {
			try {
				await deletePendingApplication({ loan_history_id: historyId }).unwrap();
				setUploadRefreshTrigger((prev) => prev + 1);
			} catch (err) {
				console.log("deletePendingApplication error:", err);
			}
		}
		handleRemoveRowbutton(item);
	};

	const handleOpenRowDeleteModal = (passedItem) => {
		setAddItemToDelete(passedItem);
		setHandleDeleteRowModel(true);
	};

	const handleUploadHistoryRowClick = (item) => {
		const status = (item?.status || "").toLowerCase().trim();
		const isPendingApplicationSubmission = status.includes("pending application submission");
		const isCompleted =
			status.includes("completed") || status.includes("created") || status.includes("submitted");

		// AURA-flow loan with a real Encompass GUID already, and not yet completed/
		// submitted: resume it in this AURA flow's Dashboard instead of the legacy
		// routes below. Explicitly excludes isCompleted so a submitted AURA loan
		// still falls through to the read-only /loan-summary branch below rather
		// than reopening as editable. An AURA-flow row with no GUID yet (upload
		// never got past "Save borrower") falls through to the existing
		// pending-item handling further down.
		if (item?.flow_type === "aura" && !isCompleted) {
			const auraGuid = getUploadHistoryItemGuid(item);
			if (auraGuid) {
				dispatch(resumeLoan({
					loanGuid: auraGuid,
					loanNumber: item?.application_number,
					borrowerName: item?.borrower_name || "Unknown Borrower",
				}));
				navigate("/current-loan/dashboard");
				return;
			}
		}

		if (isPendingApplicationSubmission) {
			const guid =
				item?.loan_guid || item?.loanGuid || item?.loan_id || item?.loanId || item?.guid;
			if (guid) {
				navigate(`/edit-loan/${guid}`);
				return;
			}
		}
		if (isCompleted) {
			const guid =
				item?.loan_guid || item?.loanGuid || item?.loan_id || item?.loanId || item?.guid;
			if (guid) {
				navigate(`/loan-summary/${guid}`);
				return;
			}
			navigate("/pipeline");
			return;
		}

		// Pending: load data and open CLEARpath success modal
		setCurrentItemSet(0);
		if (item?.load_data) {
			try {
				setLoanData(JSON.parse(item.load_data));
			} catch (_) {}
		}
		handleOpenFileUploadModal();
	};

	const breadcrumbData = [
		{ label: "Home", path: "/home" },
		{ label: "Current Loan", path: "" },
	];

	return (
		<>
			<Box>
				<CustomLayout breadcrumb={breadcrumbData} disabledOverflow={true}>
					<Stack
						direction="row"
						className={styles.stackContainer}
						sx={{ overflow: "hidden" }}
					>
						<Stack
							direction="column"
							className={`${styles.bodyContainer} ${styles.bodySpacing}`}
						>
							{/* Left panel: org + action buttons */}
							<Stack direction="column" className={styles.newLoanButtonsContainer}>
								<Stack direction="column" className={styles.verticalButtonsContainer}>
									<Stack direction="row" className={styles.buttonWithDescription}>
										<Stack direction="column" className={styles.buttonTextContainer}>
											<Body2
												text="Organization Selection *"
												extraClass={styles.buttonTitle}
											/>
											<Body3
												text="Through which organization are you delivering?"
												extraClass={styles.buttonDescription}
											/>
										</Stack>
										<Box sx={{ width: "250px" }}>
											<OrganizationDropdown extraClass={styles.newLoanOrgDropdown} />
										</Box>
									</Stack>

									<Stack direction="row" className={styles.buttonWithDescription}>
										<SecondaryButton
											startIcon={<EditOutlinedIcon sx={{ fontSize: "20px" }} />}
											text="Fill Application Manually"
											extraClass={styles.applicationTypeBtn}
											onClick={handleManualLoan}
											disabled={shouldDisableButtons}
										/>
										<Body3
											text="Begin a new application and enter all loan details manually."
											extraClass={styles.buttonDescription}
										/>
									</Stack>

									<Stack direction="row" className={styles.buttonWithDescription}>
										<SecondaryButton
											startIcon={<UploadOutlined sx={{ fontSize: "20px" }} />}
											text="Upload Single or Multiple 3.4 XML"
											extraClass={
												activeUploadView === "multi"
													? styles.selectedApplicationTypeBtn
													: styles.applicationTypeBtn
											}
											onClick={() => showMultiUploadFile()}
											disabled={shouldDisableButtons}
										/>
										<Body3
											text="Upload one or more loan files in MISMO 3.4 XML format (zip files supported)."
											extraClass={styles.buttonDescription}
										/>
									</Stack>

									<Stack direction="row" className={styles.buttonWithDescription}>
										<SecondaryButton
											startIcon={<ListAltIcon sx={{ fontSize: "20px" }} />}
											text={`Loan Upload History (${uploadHistoryLoading ? "..." : uploadHistoryCount})`}
											extraClass={
												activeUploadView === "history"
													? styles.selectedApplicationTypeBtn
													: styles.applicationTypeBtn
											}
											onClick={handleUploadHistory}
											disabled={shouldDisableButtons}
										/>
										<Body3
											text="View previously uploaded files."
											extraClass={styles.buttonDescription}
										/>
									</Stack>
								</Stack>
							</Stack>

							{/* Multi-upload area */}
							{xmlMultiUploaded && !showUploadHistory && (
								<>
									<CustomDivider extraClass={styles.dividerContainer} />
									{Object.values(multiLoanData).length > 0 &&
									Object.values(multiLoanData).some(
										(d) =>
											d &&
											(d.applications?.[0]?.borrower?.lastName ||
												d.applications?.[0]?.borrower?.mailingAddress
													?.urla2020StreetAddress)
									) ? (
										<Box sx={{ width: "100%" }}>
											<Box
												display="flex"
												alignItems="center"
												width="100%"
												className={styles.loanStack}
											>
												<Box sx={{ flex: "0 0 220px" }} />
												<Box
													sx={{
														flex: "0 0 380px",
														display: "flex",
														alignItems: "center",
														justifyContent: "flex-end",
													}}
												>
													<Typography
														variant="subtitle2"
														sx={{
															fontWeight: 600,
															display: "flex",
															alignItems: "center",
															gap: "4px",
														}}
													>
														Begin CLEARpath{" "}
														<ArrowForwardIcon
															sx={{ fontSize: "18px", verticalAlign: "middle" }}
														/>
													</Typography>
												</Box>
												<Box sx={{ flex: "0 0 80px" }} />
											</Box>
										</Box>
									) : null}

									{addCounter?.map((item) => (
										<Box key={item} sx={{ width: "100%" }}>
											<Box
												display="flex"
												alignItems="center"
												width="100%"
												className={styles.loanStack}
											>
												<Box
													display="flex"
													justifyContent="flex-start"
													sx={{ flex: "0 0 220px" }}
												>
													<SupportingDocs
														fileTypes={["XML", "zip"]}
														text="or drag to upload 3.4 XML file"
														handleFileAttach={(file) =>
															handleMultipleupload(file, item)
														}
														multiple
														flat
													/>
												</Box>
												<Box
													display="flex"
													alignItems="center"
													sx={{ flex: "0 0 280px" }}
												>
													{showLoanHistoryNumber[item] && (
														<Box sx={{ width: 280 }}>
															<PrimaryButton
																text={`${
																	multiLoanData[item]?.applications?.[0]?.borrower
																		?.lastName || "Borrower"
																} - ${
																	multiLoanData[item]?.applications?.[0]?.borrower
																		?.mailingAddress?.urla2020StreetAddress ||
																	"Unknown Address"
																}`}
																onClick={() => {
																	handleOpenFileUploadModal();
																	setCurrentItemSet(item);
																}}
																fullWidth
																sx={{
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															/>
														</Box>
													)}
												</Box>
												<Box
													display="flex"
													alignItems="center"
													sx={{ flex: "0 0 80px" }}
												>
													{showLoanHistoryNumber[item] && (
														<Box
															sx={{ cursor: "pointer" }}
															onClick={() => handleOpenRowDeleteModal(item)}
															className={styles.deleteIconContainer}
														>
															<CloseIcon className={styles.deleteRowIcon} />
														</Box>
													)}
												</Box>
											</Box>
										</Box>
									))}
									<Box sx={{ mt: 2 }}>
										<SecondaryButton
											startIcon={
												<AddCircleIcon sx={{ color: "#49787f", mr: 0.75 }} />
											}
											text="Add New Line"
											onClick={handleAddRowbutton}
											withIcon
										/>
									</Box>
								</>
							)}

							{/* Upload history table */}
							{showUploadHistory &&
								!xmlMultiUploaded &&
								!shouldDisableButtons &&
								!location?.state?.fromUploadHistory && (
									<UploadHistoryTable
										key={`${selectedOrganization}-${uploadRefreshTrigger}`}
										pageSize={25}
										maxHeight={"calc(60vh)"}
										minHeight={"calc(30vh)"}
										isFullPage={false}
										fixedHeader={true}
										onRowClick={handleUploadHistoryRowClick}
										onCountChange={setUploadHistoryCount}
										fetchCount={true}
									/>
								)}
						</Stack>
					</Stack>
				</CustomLayout>

				<ProcessingModal
					open={fileUploadModal}
					text="Your file is being uploaded and processed"
				/>
				<SuccessModal
					open={fileUploadedModal}
					handleClose={handleCloseFileUploadModal}
					text="Your 3.4 file has been accepted. Review the loan and begin CLEARpath qualification."
					confirmText="Begin CLEARpath →"
				/>
				<ConfirmationModal
					open={confirmModalVisible}
					text={confirmModalText}
					confirmText={
						confirmModalText?.includes("exit") ? "Exit Application" : "Sign In"
					}
					handleConfirm={() =>
						confirmModalText?.includes("exit")
							? navigate("/home")
							: navigate("/admin")
					}
					closeText={
						confirmModalText?.includes("exit") ? "Return to Application" : "Close"
					}
					handleClose={() =>
						confirmModalText?.includes("exit")
							? setConfirmModalVisible(false)
							: navigate(-1)
					}
				/>
				<ConfirmationModal
					open={handleDeleteRowModel}
					text={`Are you sure want to delete this application '${
						multiLoanData[addItemToDelete]?.applications?.[0]?.borrower?.lastName +
						" - " +
						multiLoanData[addItemToDelete]?.applications?.[0]?.borrower?.mailingAddress
							?.urla2020StreetAddress
					}'?`}
					confirmText="Delete application now"
					handleConfirm={handleConfirmDeleteRow}
					closeText="Close"
					handleClose={() => setHandleDeleteRowModel(false)}
				/>
				<MaxLoanModal
					open={maxLoanCheckModal}
					maxLoanAmountConstant={maxLoanAmountConstant}
					handleClose={handleCloseMaxLoanCheckModal}
					text="Your requested loan amount exceeds the max allowable loan amount. Are you sure this the correct loan amount?"
					confirmText="Save and Continue"
					setNewLoanAmount={setNewLoanAmount}
					requestedLoanAmount={multiLoanData[currentItemSet]?.borrowerRequestedLoanAmount}
					handleNewLoanAmountChange={handleNewLoanAmountChange}
				/>
			</Box>
		</>
	);
}
